import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Sort } from '@angular/material/sort';
import { CodaltComponent } from '../codalt.component';
import { Customer } from '../customer';
import { CustomerProjectType } from '../customer-project-type.enum';
import { CustomerProject } from '../customer-project.interface';
import { LocalStorage } from '../local-storage.class';
import { AuthorisationService } from '../services/auth/authorisation.service';
import { Features } from '../services/auth/features';
import { CustomerProjectService } from '../services/customer-project/customer-project.service';
import { CustomerService } from '../services/customer/customer.service';
import { PageTitleService } from '../services/page-title/page-title.service';
import { SearchService } from '../services/search/search.service';
import { Settings } from '../settings.class';
import { User } from '../user.interface';
import { Utils } from '../utils.class';

@Component({
    selector: 'app-projects',
    templateUrl: './projects.component.html',
    styleUrls: ['./projects.component.scss'],
    standalone: false,
})
export class ProjectsComponent extends CodaltComponent implements OnInit, OnDestroy {
    @ViewChild('projectManagerFilterTemplate', { static: true }) projectManagerFilterTemplate;
    projectManagerFilter: number[];
    projectManagers: User[];
    searchValue = '';
    projectFilter: (CustomerProjectType | 'archive')[] = [CustomerProjectType.regular, CustomerProjectType.maintenance];

    projects: CustomerProject[];
    customers: Customer[];
    pendingAccessCounts: Record<number, number> = {};
    onlyMyProjects = false;
    displayedColumns = [
        'customer',
        'name',
        'type',
        'consumers',
        'manager',
        'phase',
        'workarea_count',
        'latest_activity',
        'access',
        'actions',
    ];
    currentSort: Sort = {
        active: 'latest_activity',
        direction: 'desc',
    };
    private unfilteredProjects: CustomerProject[];

    constructor(
        private customerProjectService: CustomerProjectService,
        private customerService: CustomerService,
        private searchService: SearchService,
        private pageTitleService: PageTitleService
    ) {
        super();
    }

    ngOnDestroy(): void {
        this.searchService.searchTemplate = null;
        super.ngOnDestroy();
    }

    changeProjectMangerFilter() {
        this.searchAndSort();
    }

    sortProjects(sort?: Sort) {
        if (sort) {
            this.currentSort = sort;
        } else {
            sort = this.currentSort;
        }
        const data = this.projects.slice();
        if (!sort.active || sort.direction === '') {
            this.projects = data;
            return;
        }

        this.projects = data.sort((a, b) => {
            const isAsc = sort.direction === 'asc';
            if (sort.active === 'customer') {
                return Utils.compare(
                    this.customers.find(t => t.id === a?.customer_id)?.code,
                    this.customers.find(t => t.id === b?.customer_id)?.code,
                    isAsc
                );
            }
            return Utils.compare(a[sort.active], b[sort.active], isAsc);
        });
    }

    ngOnInit() {
        this.pageTitleService.setTitle('Projecten');
        this.subscriptions.add(this.customerService.getList().subscribe(customers => (this.customers = customers)));
        this.getProjects(true);
        this.subscriptions.add(
            this.searchService.change.subscribe(searchValue => {
                this.searchValue = searchValue ?? '';
                this.searchAndSort();
            })
        );
    }

    searchAndSort() {
        if (this.unfilteredProjects) {
            this.projects = this.unfilteredProjects.filter(p => {
                return (
                    (p.name.toLowerCase().indexOf(this.searchValue) !== -1 ||
                        this.customers
                            .find(t => t.id === p.customer_id)
                            ?.name.toLowerCase()
                            .indexOf(this.searchValue) !== -1 ||
                        ('' + p.manager_user?.name).toLowerCase().indexOf(this.searchValue) !== -1 ||
                        ('' + p.manager_user?.lastname).toLowerCase().indexOf(this.searchValue) !== -1) &&
                    (!this.projectManagerFilter?.length ||
                        this.projectManagerFilter.indexOf(p.manager_user?.id) !== -1) &&
                    (!this.onlyMyProjects || p.has_access !== false)
                );
            });
            this.sortProjects();
        }
    }

    getProjects(init?: boolean) {
        if (!AuthorisationService.hasFeature(Features.CustomerProjects)) {
            this.projects = [];
            this.unfilteredProjects = [];
            return;
        }

        const allProjectTypes = Settings.projectTypes.map(pt => pt.key);
        const projectType = this.projectFilter.filter(f =>
            allProjectTypes.includes(f as CustomerProjectType)
        ) as CustomerProjectType[];
        const archive = !!this.projectFilter.find(f => f === 'archive');

        if (AuthorisationService.hasFeature(Features.CustomerProjectsPendingAccessCounts)) {
            this.subscriptions.add(
                this.customerProjectService
                    .getPendingAccessCounts()
                    .subscribe(counts => (this.pendingAccessCounts = counts.per_project))
            );
        }

        this.subscriptions.add(
            this.customerProjectService.getProjects(false, undefined, projectType, archive).subscribe(projects => {
                this.unfilteredProjects = this.projects = projects;
                this.sortProjects(this.currentSort);
                this.projectManagers = [];
                this.projects
                    .filter(p => !!p.manager_user)
                    .map(p => p.manager_user)
                    .forEach(user => {
                        if (!this.projectManagers.find(u => u.id === user.id)) {
                            this.projectManagers.push(user);
                            if (init && LocalStorage.getUser()?.id === user?.id) {
                                this.projectManagerFilter = [];
                                this.projectManagerFilter.push(user.id);
                            }
                        }
                    });
                this.searchService.searchTemplate = this.projectManagerFilterTemplate;
                this.searchAndSort();
            })
        );
    }
}
